// Data Analysis Types

export interface AnalyzeRssRequest {
  rssUrl: string;
  userPrompt: string;
  systemPromptOverride?: string;
  maxResponseTokens?: number;
  temperature?: number;
}

export interface AnalyzeDelimitedDataRequest {
  delimitedData: string;
  userPrompt: string;
  delimiter?: string;
  hasHeaderRow?: boolean;
  systemPromptOverride?: string;
  maxResponseTokens?: number;
  temperature?: number;
}

export interface DataAnalysisResponse {
  analysis: string;
  insights: string[];
  recommendations?: string[];
  visualizationSuggestions?: string[];
  metadata?: Record<string, any>;
}

export interface DataAnalysisSession {
  sessionId: string;
  analysisType: 'rss' | 'csv' | 'custom';
  title: string;
  description?: string;
  sourceData?: string;
  sourceUrl?: string;
  analyses: DataAnalysisResult[];
  createdAt: string;
  updatedAt: string;
}

export interface DataAnalysisResult {
  id: string;
  prompt: string;
  analysis: string;
  insights: string[];
  recommendations?: string[];
  timestamp: string;
  temperature?: number;
  model?: string;
}

export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string;
    borderColor?: string;
  }[];
}

export interface DataPattern {
  patternType: string;
  description: string;
  confidence: number;
  affectedColumns?: string[];
  examples?: any[];
}

export interface DataSummary {
  rowCount: number;
  columnCount: number;
  columns: ColumnInfo[];
  sampleData: any[];
  patterns?: DataPattern[];
}

export interface ColumnInfo {
  name: string;
  type: string;
  uniqueValues?: number;
  nullCount?: number;
  minValue?: any;
  maxValue?: any;
  mean?: number;
  median?: number;
  mode?: any;
}

export enum AnalysisTemplate {
  GeneralInsights = 'general_insights',
  TrendAnalysis = 'trend_analysis',
  AnomalyDetection = 'anomaly_detection',
  Correlation = 'correlation',
  Forecasting = 'forecasting',
  Segmentation = 'segmentation',
  Comparison = 'comparison',
  Summary = 'summary'
}

export const ANALYSIS_TEMPLATES = [
  {
    id: AnalysisTemplate.GeneralInsights,
    name: 'General Insights',
    description: 'Extract key insights and patterns from the data',
    prompt: 'Analyze this data and provide key insights, patterns, and notable observations.'
  },
  {
    id: AnalysisTemplate.TrendAnalysis,
    name: 'Trend Analysis',
    description: 'Identify trends and changes over time',
    prompt: 'Identify and analyze trends in this data. Look for patterns over time, growth rates, seasonal variations, and predict future trends.'
  },
  {
    id: AnalysisTemplate.AnomalyDetection,
    name: 'Anomaly Detection',
    description: 'Find outliers and unusual patterns',
    prompt: 'Identify anomalies, outliers, and unusual patterns in this data. Explain what makes them unusual and their potential impact.'
  },
  {
    id: AnalysisTemplate.Correlation,
    name: 'Correlation Analysis',
    description: 'Find relationships between variables',
    prompt: 'Analyze correlations between different variables in this data. Identify strong relationships and explain their significance.'
  },
  {
    id: AnalysisTemplate.Forecasting,
    name: 'Forecasting',
    description: 'Predict future values based on historical data',
    prompt: 'Based on the historical data provided, forecast future values and trends. Provide confidence levels for your predictions.'
  },
  {
    id: AnalysisTemplate.Segmentation,
    name: 'Segmentation',
    description: 'Group data into meaningful segments',
    prompt: 'Segment this data into meaningful groups or clusters. Explain the characteristics of each segment and their differences.'
  },
  {
    id: AnalysisTemplate.Comparison,
    name: 'Comparison',
    description: 'Compare different groups or time periods',
    prompt: 'Compare different groups, categories, or time periods in this data. Highlight key differences and similarities.'
  },
  {
    id: AnalysisTemplate.Summary,
    name: 'Executive Summary',
    description: 'Create a high-level summary for executives',
    prompt: 'Create an executive summary of this data including key metrics, main findings, and actionable recommendations.'
  }
];

export interface VisualizationType {
  id: string;
  name: string;
  icon: string;
  description: string;
  dataRequirements: {
    minDimensions: number;
    maxDimensions: number;
    minMeasures: number;
    maxMeasures: number;
  };
}

export const VISUALIZATION_TYPES: VisualizationType[] = [
  {
    id: 'line',
    name: 'Line Chart',
    icon: '📈',
    description: 'Show trends over time',
    dataRequirements: {
      minDimensions: 1,
      maxDimensions: 1,
      minMeasures: 1,
      maxMeasures: 5
    }
  },
  {
    id: 'bar',
    name: 'Bar Chart',
    icon: '📊',
    description: 'Compare values across categories',
    dataRequirements: {
      minDimensions: 1,
      maxDimensions: 2,
      minMeasures: 1,
      maxMeasures: 3
    }
  },
  {
    id: 'pie',
    name: 'Pie Chart',
    icon: '🥧',
    description: 'Show proportions of a whole',
    dataRequirements: {
      minDimensions: 1,
      maxDimensions: 1,
      minMeasures: 1,
      maxMeasures: 1
    }
  },
  {
    id: 'scatter',
    name: 'Scatter Plot',
    icon: '🔵',
    description: 'Show relationships between variables',
    dataRequirements: {
      minDimensions: 0,
      maxDimensions: 1,
      minMeasures: 2,
      maxMeasures: 3
    }
  },
  {
    id: 'heatmap',
    name: 'Heatmap',
    icon: '🔥',
    description: 'Show intensity across two dimensions',
    dataRequirements: {
      minDimensions: 2,
      maxDimensions: 2,
      minMeasures: 1,
      maxMeasures: 1
    }
  }
];